/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { Nullable, Path } from "../../utils/Interfaces";
import { Box, Typography, Stack, Card, CardContent, CircularProgress, Tooltip, IconButton, createStyles } from "@mui/material";

import { useParams } from "react-router-dom";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { Person } from "@mui/icons-material";
import DataTable from "../../components/table/DataTable";
import { useWindowDimensions } from "../../utils/Hooks";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { useAppDispatch } from "../../app/hooks";
import { handleS3PDFDownload, S3PDFRoute, sanitizeFileName } from "../../utils/Functions";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LoopIcon from "@mui/icons-material/Loop";
import { makeStyles } from "@mui/styles";
import { useStorageLocationDetails } from "./storageLocationHooks";

const useStyles = makeStyles(() =>
    createStyles({
        rotateIcon: {
            animation: "$spin 1s linear infinite",
        },
        "@keyframes spin": {
            "0%": {
                transform: "rotate(360deg)",
            },
            "100%": {
                transform: "rotate(0deg)",
            },
        },
    })
);

const TechwiseOfficeDetails = () => {
    const { storageLocationId } = useParams();
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [printRequest, setPrintRequest] = useState<boolean>(false);

    const { height } = useWindowDimensions();

    const classes = useStyles();

    const { data } = useStorageLocationDetails(parseInt(storageLocationId ?? "0"));

    const partNameCol = {
        field: "material_name",
        headerName: "Part/Material",
        filterable: true,
        sortable: true,
        width: 250,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.material.name;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={params.row.material.name}>
                    <Typography variant="subtitle2" noWrap>
                        {params.row.material.name}
                    </Typography>
                </Tooltip>
            );
        },
    };

    const partManufacturerCol = {
        field: "material_manufacturer",
        headerName: "Manufacturer",
        filterable: true,
        sortable: true,
        width: 250,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.material.manufacturer;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={params.row.material.manufacturer}>
                    <Typography variant="subtitle2" noWrap>
                        {params.row.material.manufacturer}
                    </Typography>
                </Tooltip>
            );
        },
    };

    const partPartNumberCol = {
        field: "material_part_number",
        headerName: "Part #",
        filterable: true,
        sortable: true,
        width: 250,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.material.part_number;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={params.row.material.part_number}>
                    <Typography variant="subtitle2" noWrap>
                        {params.row.material.part_number}
                    </Typography>
                </Tooltip>
            );
        },
    };

    const amountCol = {
        field: "amount",
        headerName: "Amount",
        filterable: true,
        sortable: true,
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Typography variant="subtitle2" color={params.row.amount < params.row.target ? "warning.main" : "inherit"}>
                    {params.row.amount}
                </Typography>
            );
        },
    };

    const targetCol = {
        field: "target",
        headerName: "Target Amount",
        filterable: true,
        sortable: true,
        width: 150,
    };

    const notesCol = {
        field: "material_notes",
        headerName: "Notes",
        filterable: true,
        sortable: true,
        width: 400,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.material.notes;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={params.row.material.notes}>
                    <Typography variant="subtitle2" noWrap>
                        {params.row.material.notes}
                    </Typography>
                </Tooltip>
            );
        },
    };

    let locationColumns: any[] = [partNameCol, partManufacturerCol, partPartNumberCol, amountCol, targetCol, notesCol];

    let breadcrumbPaths: Nullable<Path>[] = [
        { text: "Techwise Portal", route: "/" },
        { text: "Parts/Materials", route: "/?section=materials" },
        { text: "Techwise Offices", route: "/materials/techwise-offices" },
    ];

    if (!data) {
        breadcrumbPaths.push(null);
        breadcrumbPaths.push(null);
    } else {
        breadcrumbPaths.push({
            text: data.parent.name,
            route: `/materials/techwise-office/${data.parent.id}`,
        });
        breadcrumbPaths.push({ text: data.name, route: `/materials/locations/${data.parent.id}` });
    }

    if (!data) {
        return (
            <Box>
                <StyledBreadcrumbs paths={breadcrumbPaths} />
                <Stack direction="row" justifyContent="center" sx={{ mt: 40 }}>
                    <CircularProgress size={60} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            <StyledBreadcrumbs paths={breadcrumbPaths} />

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h5">
                            {data.parent.name} / {data.name}
                        </Typography>
                        <Typography variant="overline">{data.location_type}</Typography>
                    </Stack>
                    <Box sx={{ mb: 3 }} />
                    <Stack direction="row" justifyContent="flex-start">
                        <Person />
                        <Typography sx={{ ml: 2, mt: 0.25 }}>
                            {data.responsible_user?.first_name}&nbsp;
                            {data.responsible_user?.last_name}
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            <Box sx={{ mb: 3 }} />

            <Card>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h5">Inventory (View Only)</Typography>
                        {printRequest && (
                            <Tooltip title={"Processing Print Request..."}>
                                <IconButton disabled size="large">
                                    {/* @ts-ignore */}
                                    <LoopIcon className={classes.rotateIcon} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {!printRequest && (
                            <Tooltip title={`Generate Audit Printout for ${data.name}.`}>
                                <IconButton
                                    onClick={() => {
                                        const date: Date = new Date();
                                        const dateFmt: string = `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
                                        handleS3PDFDownload(
                                            S3PDFRoute.StorageLocationAudit,
                                            data.id,
                                            dispatch,
                                            setPrintRequest,
                                            sanitizeFileName(`Inventory ${data.name} ${dateFmt}.pdf`)
                                        );
                                    }}
                                    size="large"
                                >
                                    <PictureAsPdfIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                    <Box sx={{ mb: 3 }} />
                    <DataTable
                        tableName="STORAGE_LOCATION_DETAILS"
                        allowRowSelected={false}
                        height={height - 410}
                        columns={locationColumns}
                        data={data.inventory}
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default TechwiseOfficeDetails;
